import { Box, Button, Card, Chip, Container, Grid, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { memo, useCallback, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from "src/utils/axios";
import { useSettingsContext } from "src/components/settings";
import { Rental, RentalCustomer, RentalOrganizationSettings, RentalTrailer, RentalTrailerEvent } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";
import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useParams } from "src/routes/hooks";
import TruxFormField from "src/components/inputs/trux-form-field";
import { Constants, isRentalTruckType, rentalTrailerType } from "src/constants";
import { RentalTrailerEventModal } from "./rental-trailer-event-modal";
import { RentalFileField } from "./rental-file-field";

function eventStatusChip(event: RentalTrailerEvent) {
  if (event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_IN) {
    return event.confirmedAt ? (
      <Chip size="small" color="success" label="Completed" />
    ) : (
      <Chip size="small" color="info" label="Returned - Pending Completion" />
    );
  }
  if (event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_SIGNED) {
    return <Chip size="small" color="success" label="Signed" />;
  }
  if (event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_AWAITING_SIGNATURE) {
    return <Chip size="small" color="info" label="Awaiting Signature" />;
  }
  if (
    event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_AWAITING_CONFIRMATION
  ) {
    return <Chip size="small" color="info" label="Awaiting Confirmation" />;
  }
  return <Chip size="small" color="default" label="Draft" />;
}

type RentalTrailerEventsProps = {
  events?: RentalTrailerEvent[];
  cancelledRentals?: Rental[];
  onEventSelected: (event: RentalTrailerEvent) => void;
};

// a row is either a real trailer event (Rented Out / Returned) or a rental
// that was cancelled before ever producing one, so both can share one timeline
type HistoryRow = {
  id: string;
  eventTime: string;
  type: string;
  company: string;
  name: string;
  statusChip: JSX.Element;
  signedContractUrl?: string;
  onClick?: () => void;
};

const RentalTrailerEvents = memo(({ events, cancelledRentals, onEventSelected }: RentalTrailerEventsProps) => {
  const rows = useMemo<HistoryRow[] | undefined>(() => {
    if (!events && !cancelledRentals) {
      return undefined;
    }
    const eventRows: HistoryRow[] = (events || []).map((event) => ({
      id: `event-${event.id}`,
      eventTime: event.eventTime,
      type: event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT ? "Rented Out" : "Returned",
      company: event.customerCompanyName,
      name: `${event.customerFirstName} ${event.customerLastName}`,
      statusChip: eventStatusChip(event),
      signedContractUrl: event.signedContractPdfUrl,
      onClick: () => onEventSelected(event),
    }));
    const cancelledRows: HistoryRow[] = (cancelledRentals || []).map((rental) => ({
      id: `cancelled-rental-${rental.id}`,
      eventTime: rental.updatedAt || rental.createdAt || "",
      type: "Cancelled",
      company: rental.customerCompanyName,
      name: `${rental.customerFirstName} ${rental.customerLastName}`,
      statusChip: <Chip size="small" color="error" label="Cancelled" />,
      // the agreement may already have been signed before the rental was cancelled
      signedContractUrl: rental.signedAgreementPdfUrl || undefined,
    }));
    return [...eventRows, ...cancelledRows];
  }, [events, cancelledRentals, onEventSelected]);

  const { sortedList, sortData } = useSort(rows, "eventTime", "desc");

  if (!sortedList) {
    return null;
  }

  if (!sortedList.length) {
    return <NoneFound title="No Rental History" message="This unit has not been rented out yet" />;
  }

  return (
    <Card>
      <TableContainer sx={{ position: "relative", overflow: "scroll" }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="eventTime" sortData={sortData}>
                Time
              </SortableHeader>
              <SortableHeader sortKey="type" sortData={sortData}>
                Type
              </SortableHeader>
              <SortableHeader sortKey="company" sortData={sortData}>
                Company
              </SortableHeader>
              <SortableHeader sortKey="name" sortData={sortData}>
                Name
              </SortableHeader>
              <TruxTableCell>Status</TruxTableCell>
              <TruxTableCell>Contract</TruxTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((row) => (
              <TableRow
                key={row.id}
                hover={!!row.onClick}
                onClick={row.onClick}
                sx={{ cursor: row.onClick ? "pointer" : "default" }}
              >
                <TruxTableCell>{row.eventTime ? DateTime.fromISO(row.eventTime).toFormat("MM/dd/yyyy HH:mm") : "-"}</TruxTableCell>
                <TruxTableCell>{row.type}</TruxTableCell>
                <TruxTableCell>{row.company}</TruxTableCell>
                <TruxTableCell>{row.name}</TruxTableCell>
                <TruxTableCell>{row.statusChip}</TruxTableCell>
                <TruxTableCell>
                  {row.signedContractUrl && (
                    <Button
                      href={row.signedContractUrl}
                      target="_blank"
                      rel="noopener"
                      onClick={(e) => e.stopPropagation()}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    >
                      Signed PDF
                    </Button>
                  )}
                </TruxTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
});

type PageModel = {
  rentalTrailers?: RentalTrailer[];
  events?: RentalTrailerEvent[];
  rentals?: Rental[];
  rentalCustomers?: RentalCustomer[];
  rentalOrganizationSettings?: RentalOrganizationSettings;
};

export default function Page() {
  const settings = useSettingsContext();
  const { trailerId } = useParams();

  const dataLoader = useCallback(
    () => ({
      rentalTrailers: axios.get(endpoints.rentalTrailers.findAll).then((response) => response.data),
      events: axios
        .get(formatEndpoint(endpoints.rentalTrailerEvents.byTrailerId, { trailerId }))
        .then((response) => response.data),
      rentals: axios.get(endpoints.rentals.findAll).then((response) => response.data),
      rentalCustomers: axios.get(endpoints.rentalCustomers.findAll).then((response) => response.data),
      rentalOrganizationSettings: axios.get(endpoints.rentalOrganizationSettings.current).then((response) => response.data),
    }),
    [trailerId]
  );

  const { data, updateData, error, loading, setShouldRefresh } = useModelData<PageModel>(dataLoader);

  const trailer = data?.rentalTrailers?.find((t) => t.id === Number(trailerId));
  const unitLabel = isRentalTruckType(trailer?.trailerType) ? "Truck" : "Trailer";

  const [isEventOpen, setIsEventOpen] = useState(false);

  const eventModel = useModelSavingData<RentalTrailerEvent>(
    endpoints.rentalTrailerEvents.update,
    endpoints.rentalTrailerEvents.create
  );

  const latestEvent = data?.events?.length ? data.events[0] : undefined;
  // only a signed outbound means the unit is actually out
  const isRentedOut =
    latestEvent &&
    latestEvent.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT &&
    latestEvent.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_SIGNED;
  const latestOutEvent = data?.events?.find(
    (event) => event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT
  );
  // the outbound is gated on a rental whose agreement is signed and confirmed
  const readyRental = data?.rentals?.find(
    (rental) =>
      rental.active &&
      rental.trailerId === Number(trailerId) &&
      rental.rentalStatus === Constants.RENTAL_STATUS_AGREEMENT_CONFIRMED
  );
  const cancelledRentals = data?.rentals?.filter(
    (rental) =>
      rental.trailerId === Number(trailerId) &&
      !rental.active &&
      rental.rentalStatus < Constants.RENTAL_STATUS_ACTIVE
  );

  const onEventSelected = useCallback(
    (event: RentalTrailerEvent) => {
      eventModel.set({ ...event });
      setIsEventOpen(true);
    },
    [eventModel]
  );

  const updateTrailerFile = (field: "registrationFile" | "insuranceFile" | "inspectionFile" | "liabilityInsuranceFile" | "photoUrl") =>
    (fileUrl: string) => {
      if (!trailer) {
        return;
      }
      const updated = { ...trailer, [field]: fileUrl };
      axios
        .put(endpoints.rentalTrailers.update, updated)
        .then((response) => {
          enqueueSnackbar(`Successfully saved the ${unitLabel}`);
          updateData("rentalTrailers")(
            data!.rentalTrailers!.map((t) => (t.id === trailer.id ? response.data : t))
          );
        })
        .catch(() => {
          enqueueSnackbar("The document could not be saved. Please try again.", { variant: "error" });
        });
    };

  return (
    <Container maxWidth={settings.themeStretch ? false : "lg"}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && !trailer && <NoneFound title="Unit not found" message="This unit is no longer available" />}
        {data && trailer && (
          <>
            <PageHeader title={`${unitLabel} ${trailer.trailerUnitNumber}`}>
              {/* outbound and return forms are completed in the app at the yard */}
              {isRentedOut && (
                <Typography variant="caption" color="text.secondary">
                  Rented out. The Return form is completed in the app
                </Typography>
              )}
              {!isRentedOut && readyRental && (
                <Typography variant="caption" color="text.secondary">
                  Agreement signed. The Outbound form is completed in the app
                </Typography>
              )}
            </PageHeader>

            <Card sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack gap={0.5}>
                    <Typography variant="subtitle2">Details</Typography>
                    <Typography variant="body2">{`Make / Model / Year: ${trailer.make} ${trailer.model} ${trailer.year}`}</Typography>
                    <Typography variant="body2">{`Type: ${rentalTrailerType(trailer.trailerType)?.label || "-"}`}</Typography>
                    <Typography variant="body2">{`VIN: ${trailer.vin}`}</Typography>
                    <Typography variant="body2">{`Plate: ${trailer.licensePlateState} ${trailer.licensePlate}`}</Typography>
                    <Box pt={1}>{isRentedOut ? <Chip color="info" size="small" label="Rented Out" /> : <Chip color="success" size="small" label="Available" />}</Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack gap={1}>
                    <Typography variant="subtitle2">Documents</Typography>
                    <TruxFormField label="Registration">
                      <RentalFileField fileUrl={trailer.registrationFile} onChange={updateTrailerFile("registrationFile")} />
                    </TruxFormField>
                    <TruxFormField label="Insurance">
                      <RentalFileField fileUrl={trailer.insuranceFile} onChange={updateTrailerFile("insuranceFile")} />
                    </TruxFormField>
                    <TruxFormField label="DOT Inspection">
                      <RentalFileField fileUrl={trailer.inspectionFile} onChange={updateTrailerFile("inspectionFile")} />
                    </TruxFormField>
                    <TruxFormField label="Liability Insurance">
                      <RentalFileField fileUrl={trailer.liabilityInsuranceFile} onChange={updateTrailerFile("liabilityInsuranceFile")} />
                    </TruxFormField>
                    <TruxFormField label={`${unitLabel} Photo`}>
                      <RentalFileField fileUrl={trailer.photoUrl} onChange={updateTrailerFile("photoUrl")} />
                    </TruxFormField>
                  </Stack>
                </Grid>
                {(trailer.workLog || []).length > 0 && (
                  <Grid item xs={12}>
                    <Stack gap={0.5}>
                      <Typography variant="subtitle2">Technical Work</Typography>
                      {(trailer.workLog || []).map((entry, index) => (
                        <Stack key={index} direction="row" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {`${entry.date}: ${entry.description}`}
                          </Typography>
                          {entry.fileUrl && (
                            <Button
                              href={entry.fileUrl}
                              target="_blank"
                              rel="noopener"
                              color="secondary"
                              variant="outlined"
                              size="small"
                            >
                              View
                            </Button>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Card>

            <RentalTrailerEvents events={data.events} cancelledRentals={cancelledRentals} onEventSelected={onEventSelected} />

            <RentalTrailerEventModal
              eventModel={eventModel}
              rentalCustomers={data.rentalCustomers || []}
              isOpen={isEventOpen}
              setIsOpen={setIsEventOpen}
              outboundEvent={latestOutEvent}
              organizationSettings={data.rentalOrganizationSettings}
              onSaved={() => {
                setShouldRefresh(true);
              }}
            />
          </>
        )}
      </DisplayPageData>
    </Container>
  );
}
